import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { X, Shield, Book, Clock, CheckCircle, Calendar, Pencil, Trash, Save, Mail, Phone, User, Printer, GraduationCap, ScanLine } from 'lucide-react';
import { updateMember, deleteMember, setMembers } from '../store/slices/membersSlice';
import deleteUser from '../api/DeleteUser';
import UpdateUser from '../api/UpdateUser';
import getUsers from '../api/GetUsers';
import createLoan from '../api/CreateLoan';
import returnLoan from '../api/ReturnLoan';
import getBooks from '../api/GetBooks';
import ConfirmationModal from '../components/ConfirmationModal';
import { setBooks } from '../store/slices/booksSlice';
import extendLoan from '../api/ExtendLoan';
import ItemSelectionModal from '../components/ItemSelectionModal';

const MemberDetailsModal = ({ isOpen, onClose, member, currentUser, onPrintQR }) => {
    const dispatch = useDispatch();
    const API_BASE = useSelector((state) => state.user.apiBase);
    const token = useSelector((state) => state.user.currentUser.token);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
    const delaiEmprunt = useSelector((state) => state.user.delaiEmprunt);
    const lateReturnEmailTemplate = useSelector((state) => state.user.lateReturnEmailTemplate);
    const { roleAvatars } = useSelector((state) => state.members);
    const { items: books } = useSelector((state) => state.books);
    const [isEditing, setIsEditing] = useState(false);
    const [activeLoans, setActiveLoans] = useState([]);
    const [activeReservations, setActiveReservations] = useState([]);
    const [pastLoans, setPastLoans] = useState([]);
    const [editFormData, setEditFormData] = useState(null);
    const [scanMode, setScanMode] = useState(null); // 'borrow' | 'return' | null
    const [scanQuery, setScanQuery] = useState('');
    const scanInputRef = useRef(null);
    const scanTimeoutRef = useRef(null);
    const DEFAULT_COVER = '/images/default_cover_w299.jpg';


    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null,
        isDanger: false,
        confirmText: 'Confirmer'
    });

    const [isItemSelectionOpen, setIsItemSelectionOpen] = useState(false);
    const [itemSelectionData, setItemSelectionData] = useState({ items: [], bookTitle: '', originalReservedBook: null });

    async function getLoans(API_BASE, token, id, bookList = books) {
        const headers = {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
        };

        const workRes = await fetch(`${API_BASE}/users/${id}/loans`, {
            method: "GET",
            headers,
        });

        if (!workRes.ok) {
            throw new Error("Erreur lors de la création de l'emprunt: " + (await workRes.text()));
        }

        const loans = await workRes.json();
        //console.log("liste des emprunts:", loans);
        var obj = [];
        try {
            if (loans != null) {
                for (let i = 0; i < loans.length; i++) {
                    if (!loans[i].end_date.startsWith("0001")) {
                        try {
                            obj.push({
                                id: loans[i].ID,
                                title: loans[i].item.manifestation.expression.work.title,
                                author: loans[i].item.manifestation.expression.work.contributions[0].agent.name,
                                startDate: loans[i].start_date,
                                endDate: loans[i].end_date,
                                cover: loans[i].item.manifestation.cover_url,
                            });
                        } catch (e) {
                            console.log(e);
                        }
                    }
                }
            }
        } catch (e) {
            console.log(e);
        }
        setPastLoans(obj);
        var loansBorrowed = [];
        var loansReserved = [];
        try {
            for (let i = 0; i < loans.length; i++) {
                if (loans[i].end_date.startsWith("0001")) {
                    try {
                        const book = bookList.find(b => (b.item_id || b.id) == loans[i].item_id);
                        const loanData = {
                            id: loans[i].ID,
                            item_id: loans[i].item_id,
                            title: loans[i].item.manifestation.expression.work.title,
                            author: loans[i].item.manifestation.expression.work.contributions[0].agent.name,
                            startDate: loans[i].start_date,
                            returnDate: new Date(new Date((loans[i].prolongation && !loans[i].prolongation.startsWith("0001")) ? loans[i].prolongation : loans[i].start_date).getTime() + delaiEmprunt * 24 * 60 * 60 * 1000),
                            cover: loans[i].item.manifestation.cover_url,
                        };

                        if (book && book.status === 'Réservé') {
                            loansReserved.push(loanData);
                        } else {
                            loansBorrowed.push(loanData);
                        }
                    } catch (e) {
                        console.log(e);
                    }
                }
            }
        } catch (e) {
            console.log(e);
        }
        setActiveLoans(loansBorrowed);
        setActiveReservations(loansReserved);

        // Mock data for loan history (keep existing mock data)
        /* const activeLoans = [
             { id: 1, title: "Dune", author: "Frank Herbert", dueDate: "2023-11-25", cover: "https://images.unsplash.com/photo-1541963463532-d68292c34b19?w=100&h=150&fit=crop" },
             { id: 2, title: "Atomic Habits", author: "James Clear", dueDate: "2023-11-28", cover: "https://images.unsplash.com/photo-1589829085413-56de8ae18c73?w=100&h=150&fit=crop" },
             ];
     
             const pastLoans = [
                 { id: 3, title: "Sapiens", author: "Yuval Noah Harari", returnDate: "2023-10-15", cover: "https://images.unsplash.com/photo-1531988042231-d39a9cc12a96?w=100&h=150&fit=crop" },
                 { id: 4, title: "Deep Work", author: "Cal Newport", returnDate: "2023-09-20", cover: "https://images.unsplash.com/photo-1506880018603-83d5b814b5a6?w=100&h=150&fit=crop" },
                 { id: 5, title: "Zero to One", author: "Peter Thiel", returnDate: "2023-08-10", cover: "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=100&h=150&fit=crop" },
             ];*/

        return {
            loans,
        };
    }
    useEffect(() => {
        if (member) {
            setEditFormData({
                name: member.name,
                email: member.email,
                tel: member.tel,
                classe: member.classe || '',
                role: member.role || 'Étudiant'
            });
            setIsEditing(false);
            setScanMode(null);
            setScanQuery('');
            getLoans(API_BASE, token, member.ID);
        }
    }, [member]);

    useEffect(() => {
        if (scanMode && scanInputRef.current) {
            scanInputRef.current.focus();
        }
    }, [scanMode]);

    if (!isOpen || !member) return null;

    const handleSave = async () => {
        await UpdateUser(member.ID, editFormData);
        // Reload all users
        try {
            const data = await getUsers(API_BASE, token, code_etablissement, roleAvatars);
            if (data && data.users) {
                dispatch(setMembers(data.users));
            }
        } catch (error) {
            console.error("Error reloading users:", error);
        }
        dispatch(updateMember({ ID: member.ID, ...editFormData, tel: editFormData.tel, role: editFormData.role }));
        setIsEditing(false);
    };

    const performDelete = async () => {
        const memberId = member.ID || member.id;
        if (memberId) {
            try {
                await deleteUser(memberId);
                dispatch(deleteMember(memberId));
                // Reload all users from server
                const data = await getUsers(API_BASE, token, code_etablissement, roleAvatars);
                if (data && data.users) {
                    dispatch(setMembers(data.users));
                }
            } catch (error) {
                console.error("Error deleting user:", error);
                alert("Erreur lors de la suppression : " + error.message);
            }
        }
        onClose();
    };


    const handleCreateLoan = async (rawValue) => {
        let id = rawValue;
        if (!id) return;

        if (rawValue.startsWith('ITEM:')) {
            id = rawValue.split(':')[1];
        } else if (rawValue.startsWith('USER:')) {
            return;
        }

        const itemIdInt = parseInt(id, 10);
        const userIdInt = parseInt(member.ID, 10);

        if (isNaN(itemIdInt)) {
            console.log("Invalid Item ID:", id);
            return;
        }

        //console.log("handleCreateLoan", itemIdInt);
        try {
            await createLoan(API_BASE, token, {
                item_id: itemIdInt,
                user_id: userIdInt,
                start_date: new Date().toISOString(),
                end_date: null,
                code_etablissement: code_etablissement
            });

            // Refresh loans after creation
            getLoans(API_BASE, token, member.ID);

            // Refresh books list
            try {
                const data = await getBooks(API_BASE, token, code_etablissement);
                if (data && data.work) {
                    dispatch(setBooks(data.work));
                }
            } catch (error) {
                console.error("Failed to refresh books:", error);
            }

            setScanQuery(''); // Clear input on success
        } catch (error) {
            //console.error("Loan creation failed:", error);
            if (error.message.includes("item is already on loan")) {
                setScanQuery('');
                setConfirmModal({
                    isOpen: true,
                    title: "Livre déjà emprunté",
                    message: "Ce livre est déjà emprunté par un autre membre. Voulez-vous le transférer à cet utilisateur ?",
                    onConfirm: async () => {
                        setConfirmModal(prev => ({ ...prev, isOpen: false }));
                        try {
                            // 1. Retourner l'emprunt actuel pour cet item
                            await returnLoan(API_BASE, token, {
                                item_id: itemIdInt,
                                code_etablissement: code_etablissement
                            });

                            // 2. Créer le nouvel emprunt pour le membre actuel
                            await createLoan(API_BASE, token, {
                                item_id: itemIdInt,
                                user_id: userIdInt,
                                start_date: new Date().toISOString(),
                                end_date: null,
                                code_etablissement: code_etablissement
                            });

                            // 3. Rafraîchir les emprunts du membre et la liste globale des livres
                            getLoans(API_BASE, token, member.ID);
                            const data = await getBooks(API_BASE, token, code_etablissement);
                            if (data && data.work) {
                                dispatch(setBooks(data.work));
                            }
                        } catch (transferError) {
                            console.error("Transfer failed:", transferError);
                            alert("Erreur lors du transfert de l'emprunt.");
                        }
                    },
                    confirmText: 'Transférer'
                });
            }
        }
    };
    const handleScanKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleCreateLoan(scanQuery);
            setScanQuery('');
        }
    };

    const handleDelete = () => {
        setConfirmModal({
            isOpen: true,
            title: "Supprimer le membre",
            message: `Êtes-vous sûr de vouloir supprimer le membre "${member.name}" ?`,
            onConfirm: performDelete,
            isDanger: true,
            confirmText: 'Supprimer'
        });
    };

    const handleReturnLoan = async (rawValue) => {
        let id = rawValue;
        if (!id) return;

        if (typeof rawValue === 'string' && rawValue.startsWith('ITEM:')) {
            id = rawValue.split(':')[1];
        } else if (typeof rawValue === 'string' && rawValue.startsWith('USER:')) {
            return;
        }

        try {
            await returnLoan(API_BASE, token, {
                item_id: parseInt(id, 10),
                code_etablissement: code_etablissement
            });

            // Refresh loans after return
            getLoans(API_BASE, token, member.ID);

            // Refresh books list
            try {
                const data = await getBooks(API_BASE, token, code_etablissement);
                if (data && data.work) {
                    dispatch(setBooks(data.work));
                }
            } catch (error) {
                console.error("Failed to refresh books:", error);
            }
            setScanQuery(''); // Clear input on success
        } catch (error) {
            console.error("Return failed:", error);
            alert("Erreur lors du retour du livre: " + error.message);
        }
    };



    const overdueLoans = activeLoans.filter(loan => {
        const dueDate = new Date(loan.returnDate);
        return new Date() > dueDate;
    });

    const handleSendLateEmail = () => {
        if (overdueLoans.length === 0) return;

        let bookList = "";
        if (overdueLoans.length === 1) {
            bookList = `'${overdueLoans[0].title}'`;
        } else {
            bookList = overdueLoans.map(l => `\n- ${l.title}`).join("");
        }

        let body = lateReturnEmailTemplate
            .replace(/{name}/g, member.name)
            .replace(/{title}/g, bookList);

        //Gestion des pluriels a ajuster selon le texte définit par l'école 
        if (overdueLoans.length > 1) {
            body = body.replace("Le livre", "Les livres");
            body = body.replace("est en retard", "sont en retard");
            body = body.replace("Merci de le rapporter", "Merci de les rapporter");
        }

        const subject = encodeURIComponent("Rappel : Livre(s) en retard");
        const mailtoLink = `mailto:${member.email}?subject=${subject}&body=${encodeURIComponent(body)}`;
        window.location.href = mailtoLink;
    };

    const handleExtendLoan = async (itemId) => {
        try {
            await extendLoan(API_BASE, token, itemId);
            // Refresh loans
            getLoans(API_BASE, token, member.ID);
        } catch (error) {
            console.error("Extension failed:", error);
            alert("Erreur lors de la prolongation: " + error.message);
        }
    };

    const handleCancelReservation = async (book) => {
        if (!book) return;
        try {
            // We use returnLoan to cancel the reservation record in the backend
            await returnLoan(API_BASE, token, {
                item_id: parseInt(book.item_id || book.id, 10),
                code_etablissement: code_etablissement
            });

            // Refresh loans and books list
            const data = await getBooks(API_BASE, token, code_etablissement);
            const freshBooks = (data && data.work) ? data.work : books;

            if (data && data.work) {
                dispatch(setBooks(data.work));
            }

            await getLoans(API_BASE, token, member.ID, freshBooks);
        } catch (error) {
            console.error("Cancel reservation failed:", error);
            alert("Erreur lors de l'annulation de la réservation.");
        }
    };

    const handleConfirmReservation = async (book) => {
        if (!book) return;

        // Check if there are multiple items for this manifestation
        const manifestationId = book.manifestation_id;
        const relatedItems = books.filter(b => b.manifestation_id === manifestationId);

        if (relatedItems.length > 1) {
            setItemSelectionData({
                items: relatedItems,
                bookTitle: book.title,
                originalReservedBook: book
            });
            setIsItemSelectionOpen(true);
            return;
        }

        // Only one item, proceed as before
        confirmReservationProcess(book, book);
    };

    const confirmReservationProcess = async (originalReservedBook, selectedItem) => {
        try {
            // 1. Cancel the old reservation-type loan (the one originally reserved)
            await returnLoan(API_BASE, token, {
                item_id: parseInt(originalReservedBook.item_id || originalReservedBook.id, 10),
                code_etablissement: code_etablissement
            });

            // 2. Create a new standard loan for the member using the SELECTED item
            await createLoan(API_BASE, token, {
                item_id: parseInt(selectedItem.item_id || selectedItem.id, 10),
                user_id: parseInt(member.ID, 10),
                start_date: new Date().toISOString(),
                end_date: null,
                code_etablissement: code_etablissement
            });

            // 3. Refresh everything
            const data = await getBooks(API_BASE, token, code_etablissement);
            const freshBooks = (data && data.work) ? data.work : books;

            if (data && data.work) {
                dispatch(setBooks(data.work));
            }

            await getLoans(API_BASE, token, member.ID, freshBooks);

            // Close selection modal if open
            setIsItemSelectionOpen(false);
        } catch (error) {
            console.error("Confirm reservation failed:", error);
            alert("Erreur lors de la confirmation de l'emprunt.");
        }
    };

    const handleItemSelect = (selectedItem) => {
        confirmReservationProcess(itemSelectionData.originalReservedBook, selectedItem);
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto lg:pl-72">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-background border border-border rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
                <div className="p-6 border-b border-border flex items-center justify-between bg-muted/50">
                    <div className="flex items-center gap-4 flex-1">
                        <img src={member.avatar} alt={member.name} className="w-16 h-16 rounded-full border-2 border-primary/50" />
                        <div className="flex-1">
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editFormData?.name || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                    className="w-full bg-muted border border-border rounded-lg px-3 py-1 text-foreground font-bold text-xl mb-2"
                                    placeholder="Nom du membre"
                                />
                            ) : (
                                <h2 className="text-2xl font-bold text-foreground mb-1">{member.name}</h2>
                            )}

                            <div className="flex flex-col gap-1 text-muted-foreground text-sm">
                                <div className="flex items-center gap-2">
                                    <Shield size={14} className="text-primary" />
                                    {isEditing ? (
                                        <select
                                            value={editFormData?.role || 'Étudiant'}
                                            onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                                            className="bg-muted border border-border rounded px-2 py-0.5 text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                                        >
                                            <option value="Étudiant">Étudiant</option>
                                            <option value="Enseignant">Enseignant</option>
                                            <option value="Bibliothécaire">Bibliothécaire</option>
                                        </select>
                                    ) : (
                                        <span>{member.role}</span>
                                    )}
                                </div>

                                {isEditing ? (
                                    <>
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Mail size={14} />
                                            <input
                                                type="email"
                                                value={editFormData?.email || ''}
                                                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                                                className="bg-muted border border-border rounded px-2 py-0.5 text-foreground text-sm w-full max-w-xs"
                                                placeholder="Email"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Phone size={14} />
                                            <input
                                                type="tel"
                                                value={editFormData?.tel || ''}
                                                onChange={(e) => setEditFormData({ ...editFormData, tel: e.target.value })}
                                                className="bg-muted border border-border rounded px-2 py-0.5 text-foreground text-sm w-full max-w-xs"
                                                placeholder="Téléphone"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <GraduationCap size={14} />
                                            <input
                                                type="text"
                                                value={editFormData?.classe || ''}
                                                onChange={(e) => setEditFormData({ ...editFormData, classe: e.target.value })}
                                                className="bg-muted border border-border rounded px-2 py-0.5 text-foreground text-sm w-full max-w-xs"
                                                placeholder="Classe (ex: 3ème B)"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className="space-y-1 text-muted-foreground">
                                        <div className="flex items-center gap-2">
                                            <Mail size={14} />
                                            <span>{member.email}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Phone size={14} />
                                            <span>{member.tel}</span>
                                        </div>
                                        {member.classe && (
                                            <div className="flex items-center gap-2">
                                                <GraduationCap size={14} />
                                                <span>{member.classe}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {currentUser?.role === 'Bibliothécaire' && (
                            <>
                                {isEditing ? (
                                    <>
                                        <button
                                            onClick={handleSave}
                                            className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                                            title="Enregistrer"
                                        >
                                            <Save size={20} />
                                        </button>
                                        <button
                                            onClick={() => setIsEditing(false)}
                                            className="p-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                                            title="Annuler"
                                        >
                                            <X size={20} />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            onClick={() => onPrintQR(member)}
                                            className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                            title="Imprimer QR Code"
                                        >
                                            <Printer size={20} />
                                        </button>
                                        <button
                                            onClick={() => setIsEditing(true)}
                                            className="p-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors"
                                            title="Modifier"
                                        >
                                            <Pencil size={20} />
                                        </button>
                                        <button
                                            onClick={handleDelete}
                                            className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                            title="Supprimer"
                                        >
                                            <Trash size={20} />
                                        </button>
                                    </>
                                )}
                                <div className="w-px h-6 bg-white/10 mx-1" />
                            </>
                        )}
                        {overdueLoans.length > 0 && currentUser.role !== "Étudiant" && (
                            <button
                                onClick={handleSendLateEmail}
                                className="flex items-center gap-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-lg shadow-red-500/20"
                                title="Envoyer un rappel par email"
                            >
                                <Mail size={18} />
                                <span className="hidden sm:inline font-medium text-sm text-white">Relancer par email</span>
                            </button>
                        )}
                        <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-colors">
                            <X size={24} />
                        </button>
                    </div>
                </div>

                <div className="p-6 overflow-y-auto">
                    {currentUser?.role !== 'Étudiant' && (
                        <div className="mb-8 p-4 bg-muted/30 rounded-xl border border-border">
                            <div className="flex gap-4 mb-4">
                                <button
                                    onClick={() => setScanMode('borrow')}
                                    className={`flex-1 py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all ${scanMode === 'borrow'
                                        ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25 ring-2 ring-primary/50'
                                        : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                                        }`}
                                >
                                    <Book size={20} />
                                    <span className="font-medium">Emprunter un livre</span>
                                </button>
                                <button
                                    onClick={() => setScanMode('return')}
                                    className={`flex-1 py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all ${scanMode === 'return'
                                        ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25 ring-2 ring-orange-500/50'
                                        : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                                        }`}
                                >
                                    <CheckCircle size={20} />
                                    <span className="font-medium">Rendre un livre</span>
                                </button>
                            </div>

                            {scanMode && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="relative flex flex-col gap-3 mt-4"
                                >
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <ScanLine size={18} className={scanMode === 'borrow' ? 'text-blue-500' : 'text-emerald-500'} />
                                        </div>
                                        <input
                                            ref={scanInputRef}
                                            type="text"
                                            value={scanQuery}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                setScanQuery(newValue);

                                                if (scanTimeoutRef.current) {
                                                    clearTimeout(scanTimeoutRef.current);
                                                }

                                                scanTimeoutRef.current = setTimeout(() => {
                                                    if (scanMode === 'borrow') handleCreateLoan(newValue);
                                                    else if (scanMode === 'return') handleReturnLoan(newValue);
                                                }, 300);
                                            }}
                                            placeholder={scanMode === 'borrow' ? "Scannez le code-barres..." : "Scannez le code-barres..."}
                                            className="w-full bg-background border border-border rounded-xl py-3 pl-10 pr-10 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        />
                                        <button
                                            onClick={() => { setScanMode(null); setScanQuery(''); }}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <div className="relative flex-1">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Book size={18} className="text-muted-foreground" />
                                            </div>
                                            <input
                                                type="number"
                                                id="manualIdInput"
                                                placeholder="Ou entrez l'ID du livre manuellement..."
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && e.target.value) {
                                                        const val = e.target.value;
                                                        if (scanMode === 'borrow') handleCreateLoan(val);
                                                        else if (scanMode === 'return') handleReturnLoan(val);
                                                        e.target.value = '';
                                                    }
                                                }}
                                                className="w-full bg-background border border-border rounded-xl py-3 pl-10 pr-4 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                            />
                                        </div>
                                        <button
                                            onClick={() => {
                                                const input = document.getElementById('manualIdInput');
                                                if (input && input.value) {
                                                    const val = input.value;
                                                    if (scanMode === 'borrow') handleCreateLoan(val);
                                                    else if (scanMode === 'return') handleReturnLoan(val);
                                                    input.value = '';
                                                }
                                            }}
                                            className="px-4 py-3 bg-primary text-primary-foreground font-medium rounded-xl hover:bg-primary/90 transition-colors"
                                        >
                                            Valider
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    )}

                    <div className="mb-8">
                        <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                            <Book size={20} className="text-primary" />
                            Emprunts en cours
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {activeLoans.map((book) => {
                                const isOverdue = new Date() > new Date(book.returnDate);
                                return (
                                    <div key={book.id} className="flex gap-3 bg-muted/30 p-3 rounded-xl border border-border">
                                        <img onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = DEFAULT_COVER;
                                        }} src={book.cover || DEFAULT_COVER} alt={book.title} className="w-12 h-16 object-cover rounded-md" />
                                        <div className="flex-1">
                                            <h4 className="font-bold text-foreground text-sm">{book.title}</h4>
                                            <p className="text-muted-foreground text-xs mb-2">{book.author}</p>
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                    <Clock size={12} />
                                                    <span>Emprunté le {new Date(book.startDate).toLocaleDateString() + " à " + new Date(book.startDate).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", hour12: false, })}</span>
                                                </div>
                                                <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md w-fit ${isOverdue ? "text-red-400 bg-red-400/10" : "text-orange-400 bg-orange-400/10"}`}>
                                                    <Clock size={12} />
                                                    <span>Retour avant: {new Date(book.returnDate).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                        {currentUser?.role !== 'Étudiant' && (
                                            <div className="flex flex-col gap-2 self-start">
                                                <button
                                                    onClick={() => handleReturnLoan(book.item_id)}
                                                    className="px-3 py-1 text-xs bg-muted hover:bg-muted/80 text-muted-foreground rounded-lg transition-colors border border-border"
                                                >
                                                    Rendre
                                                </button>
                                                {isOverdue && (
                                                    <button
                                                        onClick={() => handleExtendLoan(book.item_id)}
                                                        className="px-3 py-1 text-xs bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg transition-colors border border-blue-500/20"
                                                    >
                                                        Prolonger
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {activeReservations.length > 0 && (
                        <div className="mb-8">
                            <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                                <Calendar size={20} className="text-amber-500" />
                                Réservations en cours
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {activeReservations.map((res) => {
                                    const book = books.find(b => (b.item_id || b.id) == res.item_id);
                                    return (
                                        <div key={res.id} className="flex gap-3 bg-amber-500/5 p-3 rounded-xl border border-amber-500/20">
                                            <img onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = DEFAULT_COVER;
                                            }} src={res.cover || DEFAULT_COVER} alt={res.title} className="w-12 h-16 object-cover rounded-md" />
                                            <div className="flex-1">
                                                <h4 className="font-bold text-foreground text-sm">{res.title}</h4>
                                                <p className="text-muted-foreground text-xs mb-2">{res.author}</p>
                                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                    <Calendar size={12} />
                                                    <span>Réservé le {new Date(res.startDate).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                            {(currentUser?.role === 'Bibliothécaire' || (currentUser?.role === 'Étudiant' && currentUser.ID == member.ID)) && (
                                                <div className="flex flex-col gap-2 self-start">
                                                    {currentUser?.role === 'Bibliothécaire' && (
                                                        <button
                                                            onClick={() => handleConfirmReservation(book)}
                                                            className="px-3 py-1 text-xs bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 rounded-lg transition-colors border border-emerald-500/20"
                                                        >
                                                            Confirmer l'emprunt
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleCancelReservation(book)}
                                                        className="px-3 py-1 text-xs bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 rounded-lg transition-colors border border-amber-500/20"
                                                    >
                                                        Annuler
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div>
                        <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                            <CheckCircle size={20} className="text-emerald-500" />
                            Historique
                        </h3>
                        <div className="space-y-3">
                            {pastLoans.sort((a, b) => new Date(b.endDate) - new Date(a.endDate)).map((book) => (
                                <div key={book.id} className="flex items-center justify-between bg-muted/30 p-3 rounded-xl border border-border hover:bg-muted/50 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <img onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = DEFAULT_COVER;
                                        }} src={book.cover || DEFAULT_COVER} alt={book.title} className="w-10 h-14 object-cover rounded-md grayscale opacity-70" />
                                        <div>
                                            <h4 className="font-medium text-foreground text-sm">{book.title}</h4>
                                            <p className="text-muted-foreground text-xs">{book.author}</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <Calendar size={12} />
                                            <span>Emprunté le {new Date(book.startDate).toLocaleDateString() + " à " + new Date(book.startDate).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", hour12: false, })}</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <Calendar size={12} />
                                            <span>Rendu le {new Date(book.endDate).toLocaleDateString() + " à " + new Date(book.endDate).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", hour12: false, })}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.div>

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                isDanger={confirmModal.isDanger}
                confirmText={confirmModal.confirmText}
            />

            <ItemSelectionModal
                isOpen={isItemSelectionOpen}
                onClose={() => setIsItemSelectionOpen(false)}
                onSelect={handleItemSelect}
                items={itemSelectionData.items}
                bookTitle={itemSelectionData.bookTitle}
            />
        </div>
    );
};

export default MemberDetailsModal;
